/* First slide is complete in Liquid. Enhance navigation without waiting for window.load. */
(() => {
  if (customElements.get('nursena-carousel')) return;

  class NursenaCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('.nbs-track');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.dots = [...this.querySelectorAll('.nbs-dot')];
      this.toggle = this.querySelector('.nbs-toggle');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.wantsPlay = false;
      this.canAutoStart = !this.reducedMotion.matches;
      this.visible = !('IntersectionObserver' in window);
      this.hovered = false;
      this.focused = false;
      this.touching = false;
      this.controller = new AbortController();
      const on = (target, name, callback, options = {}) => {
        target.addEventListener(name, callback, { ...options, signal: this.controller.signal });
      };

      this.style.setProperty('--nbs-dur', '6000ms');
      this.setActive(0);
      if (this.toggle) {
        this.toggle.hidden = this.reducedMotion.matches;
        on(this.toggle, 'click', () => {
          this.canAutoStart = false;
          this.wantsPlay = !this.wantsPlay;
          // Explicit play can resume while the play button has keyboard focus.
          if (this.wantsPlay) this.focused = false;
          this.schedule();
        });
      }
      this.dots.forEach((dot, index) => on(dot, 'click', () => this.go(index)));
      const previous = this.querySelector('.nbs-prev');
      const next = this.querySelector('.nbs-next');
      if (previous) on(previous, 'click', () => this.go(this.index - 1));
      if (next) on(next, 'click', () => this.go(this.index + 1));

      on(this, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        if (event.target.matches('input, textarea, select')) return;
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      on(this.track, 'scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          if (!this.track.clientWidth) return;
          const index = Math.max(0, Math.min(this.slides.length - 1,
            Math.round(this.track.scrollLeft / this.track.clientWidth)));
          if (index !== this.index) {
            this.hydrate(index);
            this.setActive(index);
            this.schedule();
          }
        }, 100);
      }, { passive: true });
      on(this, 'pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.schedule();
      });
      on(this, 'pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = false;
        this.schedule();
      });
      on(this, 'focusin', () => {
        this.focused = true;
        this.schedule();
      });
      on(this, 'focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.schedule();
      });
      on(this.track, 'pointerdown', (event) => {
        this.hydrate(this.index - 1);
        this.hydrate(this.index + 1);
        if (event.pointerType !== 'touch') return;
        this.touching = true;
        this.schedule();
      }, { passive: true });
      const endTouch = () => {
        if (!this.touching) return;
        this.touching = false;
        this.schedule();
      };
      on(window, 'pointerup', endTouch, { passive: true });
      on(window, 'pointercancel', endTouch, { passive: true });
      on(this, 'pointerup', (event) => {
        if (!this.canAutoStart || event.target.closest('.nbs-toggle')) return;
        // A page scroll or pointer movement must not start rotating the LCP image.
        this.canAutoStart = false;
        this.wantsPlay = true;
        this.schedule();
      }, { passive: true });
      on(this.track, 'wheel', () => {
        this.hydrate(this.index - 1);
        this.hydrate(this.index + 1);
      }, { passive: true });
      on(document, 'visibilitychange', () => this.schedule());
      on(this.reducedMotion, 'change', () => {
        if (this.reducedMotion.matches) {
          this.wantsPlay = false;
          this.canAutoStart = false;
        }
        if (this.toggle) this.toggle.hidden = this.reducedMotion.matches;
        this.schedule();
      });
      on(window, 'resize', () => {
        this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: 'instant' });
      }, { passive: true });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          this.schedule();
        }, { threshold: 0.1 });
        this.observer.observe(this);
      }
      this.schedule();
    }

    hydrate(index) {
      const slide = this.slides[(index + this.slides.length) % this.slides.length];
      slide.querySelectorAll('img[data-nbs-src]').forEach((image) => {
        // Start a requested slide now, rather than relying on horizontal lazy thresholds.
        image.loading = 'eager';
        if (image.dataset.nbsSrcset) image.srcset = image.dataset.nbsSrcset;
        image.src = image.dataset.nbsSrc;
        image.removeAttribute('data-nbs-src');
        image.removeAttribute('data-nbs-srcset');
      });
    }

    setActive(index) {
      this.index = index;
      this.slides.forEach((slide, position) => {
        slide.classList.toggle('is-active', position === index);
        slide.inert = position !== index;
        slide.setAttribute('aria-hidden', String(position !== index));
      });
      this.dots.forEach((dot, position) => {
        dot.classList.toggle('is-active', position === index);
        if (position === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    go(index) {
      index = (index + this.slides.length) % this.slides.length;
      this.hydrate(index);
      this.setActive(index);
      this.track.scrollTo({
        left: index * this.track.clientWidth,
        behavior: this.reducedMotion.matches ? 'instant' : 'smooth',
      });
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      const running = this.wantsPlay && !this.reducedMotion.matches;
      const paused = document.hidden || !this.visible || this.hovered || this.focused || this.touching;
      this.classList.toggle('is-run', running);
      this.classList.toggle('is-paused', paused);
      if (this.toggle) {
        this.toggle.setAttribute('aria-pressed', String(running));
        this.toggle.setAttribute('aria-label', running ? 'Otomatik geçişi durdur' : 'Otomatik geçişi başlat');
      }
      this.track.setAttribute('aria-live', running ? 'off' : 'polite');
      if (!running || paused) return;
      this.hydrate(this.index + 1);
      this.timer = setTimeout(() => this.go(this.index + 1), 6000);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.scrollTimer);
    }
  }

  customElements.define('nursena-carousel', NursenaCarousel);
})();
